// src/contextStore/AllPostContext.js
import React, { createContext, useState, useEffect, useCallback } from "react";
import client from "../api/client";

export const AllPostContext = createContext(null);

/**
 * AllPostProvider
 * - Loads public listings from /api/machines
 * - Exposes helpers: reload(), loadMore(), setAllPost (for manual updates)
 *
 * Response expectation (backend):
 * GET /api/machines?page=1&limit=20&search=... => { ok: true, machines: [...], total: 123 }
 *
 * Adjust query params as needed to match your backend API.
 */
export function AllPostProvider({ children, initialPage = 1, pageSize = 20 }) {
  const [allPost, setAllPost] = useState([]);
  const [page, setPage] = useState(initialPage);
  const [limit, setLimit] = useState(pageSize);
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [total, setTotal] = useState(null);
  const [error, setError] = useState(null);
  const [lastQuery, setLastQuery] = useState({}); // store last search/filter params

  // low-level fetcher
  const fetchPosts = useCallback(
    async (opts = {}) => {
      const { page: p = 1, limit: l = limit, replace = false, query = {} } = opts;
      setLoading(true);
      setError(null);

      // build query params object
      const params = { page: p, limit: l, ...query };

      try {
        const res = await client.get("/machines", { params });
        // adjust to your backend response shape
        // expecting { ok: true, machines: [...], total: <number> }
        if (!res || !res.data) throw new Error("Empty response from server");

        // best-effort parsing:
        const machines = res.data.machines || res.data.items || res.data.data || [];
        const tot = typeof res.data.total === "number" ? res.data.total : (res.data.count || null);

        if (replace) {
          setAllPost(machines);
        } else {
          setAllPost((prev) => {
            // avoid duplicates on append (by id)
            const ids = new Set(prev.map((x) => String(x.id || x._id || x._id)));
            const toAdd = machines.filter((m) => !ids.has(String(m.id || m._id || m._id)));
            return [...prev, ...toAdd];
          });
        }

        setPage(p);
        setLimit(l);
        setTotal(tot);
        // if total known, determine hasMore. Otherwise based on returned length.
        if (tot !== null) {
          setHasMore((p * l) < tot);
        } else {
          setHasMore(machines.length === l);
        }

        setLastQuery(query);
        setLoading(false);
        return { machines, total: tot };
      } catch (err) {
        console.error("Failed to fetch machines:", err);
        setError(err?.response?.data?.error || err.message || "Failed to load listings");
        setLoading(false);
        return { machines: [], total: null, error: err };
      }
    },
    [limit]
  );

  // initial load on mount
  useEffect(() => {
    let mounted = true;
    (async () => {
      if (!mounted) return;
      await fetchPosts({ page: initialPage, limit: pageSize, replace: true });
    })();
    return () => {
      mounted = false;
    };
  }, [fetchPosts, initialPage, pageSize]);

  // public helpers
  const reload = useCallback(
    async (opts = {}) => {
      // opts can include query, page, limit
      const { page: p = 1, limit: l = pageSize, query = {} } = opts;
      return fetchPosts({ page: p, limit: l, replace: true, query });
    },
    [fetchPosts, pageSize]
  );

  const loadMore = useCallback(async () => {
    if (!hasMore || loading) return null;
    const nextPage = page + 1;
    return fetchPosts({ page: nextPage, limit, replace: false, query: lastQuery });
  }, [fetchPosts, hasMore, loading, page, limit, lastQuery]);

  /**
   * appendOne: convenient helper to insert a newly created machine to front
   * - useful to call from Create.js after successful creation
   */
  const appendOne = useCallback((machine) => {
    if (!machine) return;
    setAllPost((prev) => {
      const ids = new Set(prev.map((x) => String(x.id || x._id || x._id)));
      if (ids.has(String(machine.id || machine._id))) return prev;
      return [machine, ...prev];
    });
  }, []);

  return (
    <AllPostContext.Provider
      value={{
        allPost,
        setAllPost,
        loading,
        error,
        page,
        limit,
        total,
        hasMore,
        reload,
        loadMore,
        appendOne,
      }}
    >
      {children}
    </AllPostContext.Provider>
  );
}
